/**
 * Planos Studio AI service.
 *
 * The single AI call (`detectRoomFromImage`) used to be made directly from
 * the browser via `new GoogleGenAI(...)`, which required `VITE_API_KEY` to
 * be baked into the public JS bundle. It now routes through the
 * `planos-detect-room` Supabase Edge Function so the Gemini key stays
 * server-side. The exported function signature is preserved exactly so
 * call sites (`PlanosEditor.tsx`) are unchanged.
 */

import { supabase } from './supabase';

export interface RoomDetectionResult {
  room_type: string;
  room_label: string;
  confidence: number;
  description: string;
  features: string[];
}

export const detectRoomFromImage = async (imageBase64: string): Promise<RoomDetectionResult> => {
  const { data, error } = await supabase.functions.invoke('planos-detect-room', {
    body: { imageBase64 },
  });

  if (error) {
    let message = error.message || 'Room detection failed';
    try {
      const ctxRes = (error as { context?: Response }).context;
      if (ctxRes?.json) {
        const payload = await ctxRes.clone().json() as { error?: string };
        if (payload?.error) message = payload.error;
      }
    } catch {
      // fall through with default message
    }
    throw new Error(message);
  }

  const result = (data as { success?: boolean; result?: RoomDetectionResult } | null)?.result;
  if (!result) {
    throw new Error('No detection result returned from server');
  }

  return {
    room_type: result.room_type || 'room',
    room_label: result.room_label || 'Room',
    confidence: typeof result.confidence === 'number' ? result.confidence : 0.5,
    description: result.description || '',
    features: Array.isArray(result.features) ? result.features : [],
  };
};

export const imageUrlToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
